import { useGetAllAdminUserQuery } from '@codezo/graphql';
import { useState } from 'react';
import { Popover, PopoverContent } from '../ui/popover';
import {
	SelectSearchInput,
	SelectTrigger,
	type SelectTriggerVariant,
} from '../ui/select-primitives';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface AdminUserSelectorProps {
	value: string[];
	onChange: (v: string[]) => void;
	placeholder?: string;
	skip?: boolean;
	/** Pre-computed display label for the current selection (used before the user picks new items) */
	selectedLabel?: string;
	/** Visual treatment: `form` (default) or `toolbar` for list-page filters */
	variant?: SelectTriggerVariant;
	/** Extra classes merged into the trigger (e.g. width on a toolbar). */
	className?: string;
}

export function AdminUserSelector({
	value,
	onChange,
	placeholder = 'Select admins',
	skip = false,
	selectedLabel: selectedLabelProp,
	variant = 'form',
	className,
}: AdminUserSelectorProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);
	const [pickedLabels, setPickedLabels] = useState<Record<string, string>>({});

	const { data, loading } = useGetAllAdminUserQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
			},
		},
		fetchPolicy: 'no-cache',
		skip: skip || !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllAdminUser?.data,
		pageInfo: data?.getAllAdminUser?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const toggle = (id: string, label: string) => {
		if (value.includes(id)) {
			onChange(value.filter((v) => v !== id));
		} else {
			onChange([...value, id]);
			setPickedLabels((prev) => ({ ...prev, [id]: label }));
		}
	};

	const displayLabel =
		value.length > 0
			? value
					.map((id) => pickedLabels[id])
					.filter(Boolean)
					.join(', ') ||
				selectedLabelProp ||
				null
			: null;

	const filteredItems = query
		? scroll.items.filter((a) =>
				`${a.firstName} ${a.lastName}`.toLowerCase().includes(query.toLowerCase())
			)
		: scroll.items;

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) {
					setQuery('');
					scroll.reset();
					setPage(1);
				}
			}}
		>
			<SelectTrigger
				label={displayLabel}
				placeholder={placeholder}
				hasValue={value.length > 0}
				variant={variant}
				className={className}
				count={value.length}
			/>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<SelectSearchInput value={query} onChange={setQuery} />
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{value.length > 0 && (
						<button
							onClick={() => onChange([])}
							className="mb-0.5 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left text-2xs text-text-4 transition-colors hover:text-text-2"
						>
							Clear all
						</button>
					)}
					{scroll.initialLoading && (
						<div className="py-4 text-center text-xs text-text-4">Loading…</div>
					)}
					{!loading && filteredItems.length === 0 && (
						<div className="px-2 py-3 text-center text-sm text-text-4">
							{query ? 'No results' : 'No admins'}
						</div>
					)}
					{filteredItems.map((admin) => {
						const selected = value.includes(admin.id);
						return (
							<button
								key={admin.id}
								onClick={() =>
									toggle(admin.id, `${admin.firstName} ${admin.lastName}`)
								}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
									selected
										? 'bg-surface-fill text-text-1'
										: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
								)}
							>
								<Checkbox
									checked={selected}
									tabIndex={-1}
									className="pointer-events-none"
								/>
								{admin.firstName} {admin.lastName}
							</button>
						);
					})}
					{scroll.loadingMore && (
						<div className="py-1 text-center text-2xs text-text-4">Loading more…</div>
					)}
					<div ref={scroll.sentinelRef} className="h-1" />
				</div>
			</PopoverContent>
		</Popover>
	);
}
